import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";

function BlogCarousel({ blogPosts }: { blogPosts: any[] }) {
  const gap = 24;
  const extendedPosts = [...blogPosts, ...blogPosts];
  const total = blogPosts.length;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [cardPx, setCardPx] = useState(400);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const calc = () => {
      const visible =
        window.innerWidth < 600 ? 1 : window.innerWidth < 1024 ? 2 : 3;
      if (containerRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        setCardPx((containerWidth - gap * (visible - 1)) / visible);
      }
    };
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setCurrentIndex((p) => p + 1), 3500);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (currentIndex >= total) {
      const t = setTimeout(() => setCurrentIndex(0), 600);
      return () => clearTimeout(t);
    }
  }, [currentIndex, total]);

  const next = () => setCurrentIndex((p) => p + 1);
  const prev = () => setCurrentIndex((p) => (p === 0 ? total - 1 : p - 1));

  return (
    <Box sx={{ width: "100%", position: "relative" }}>
      {/* ── Navigation Arrows (Absolute Positioned or Floating) ── */}
      {/* Note: If you want these inside the Home section header, 
          move this block to the Home component and pass setCurrentIndex as a prop */}
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          position: "absolute",
          top: { xs: -70, md: -95 }, // Adjusting to sit next to the 'Blogs' title in parent
          right: 0,
        }}
      >
        <IconButton onClick={prev} sx={navButtonStyle}>
          &#8249;
        </IconButton>
        <IconButton onClick={next} sx={navButtonStyle}>
          &#8250;
        </IconButton>
      </Box>

      {/* ── Carousel track ── */}
      <Box ref={containerRef} sx={{ overflow: "hidden", width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            gap: `${gap}px`,
            transform: `translateX(-${currentIndex * (cardPx + gap)}px)`,
            transition:
              currentIndex >= total
                ? "none"
                : "transform 0.55s cubic-bezier(0.4,0,0.2,1)",
          }}
        >
          {extendedPosts.map((post, index) => (
            <Box
              key={index}
              component={Link}
              to={post.href}
              sx={{
                flex: `0 0 ${cardPx}px`,
                textDecoration: "none",
                display: "block",
                borderRadius: "24px 24px 0px 24px",
                overflow: "hidden",
                boxShadow: "0 4px 20px rgba(0,0,0,0.10)",
                transition: "box-shadow 0.3s, transform 0.3s",
                "&:hover": {
                  boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
                  transform: "translateY(-4px)",
                },
              }}
            >
              <Box
                component="img"
                src={post.img.src}
                alt={post.img.alt}
                sx={{
                  width: "100%",
                  height: "auto",
                  display: "block",
                  objectFit: "cover",
                }}
              />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

const navButtonStyle = {
  width: { xs: 40, md: 48 },
  height: { xs: 40, md: 48 },
  border: "1px solid",
  borderColor: "divider",
  color: "text.primary",
  "&:hover": {
    bgcolor: "primary.main",
    color: "common.white",
    borderColor: "primary.main",
  },
};

export default BlogCarousel;
